import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Heart, Users, Calendar, MapPin, Image, X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = {
  1:'Mariage', 2:'Mariage civil', 3:'Fiançailles', 4:'Divorce',
  12:'Naissance', 13:'Décès', 14:'Sépulture', 16:'Baptême',
};

const parseDate = (dateObj) => {
  if (!dateObj || typeof dateObj !== 'object') return '';
  const dv = dateObj.dateval;
  if (Array.isArray(dv) && dv.length >= 3) {
    const [j, m, a] = dv;
    return [j, m, a].filter(Boolean).join('/');
  }
  return dateObj.text || '';
};

const PersonLink = ({ handle, given_name, surname }) => (
  <Link to={`/persons/${handle}`}
    className="inline-flex items-center gap-1.5 text-forest hover:underline font-medium">
    {given_name} {surname}
  </Link>
);


// ── Visionneuse image plein écran ─────────────────────────────────────────────
const LightBox = ({ images, index, onClose }) => {
  const [current, setCurrent] = useState(index);
  const [scale, setScale]     = useState(1);

  const img  = images[current];
  const prev = () => { setCurrent(i => (i - 1 + images.length) % images.length); setScale(1); };
  const next = () => { setCurrent(i => (i + 1) % images.length); setScale(1); };
  const zoomIn  = () => setScale(s => Math.min(s + 0.25, 4));
  const zoomOut = () => setScale(s => Math.max(s - 0.25, 0.25));

  // Clavier : ← → Échap
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Molette pour zoomer
  const handleWheel = (e) => {
    e.preventDefault();
    setScale(s => Math.min(Math.max(s - e.deltaY * 0.001, 0.25), 4));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90"
      onClick={onClose}
    >
      {/* Barre haute */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 z-10"
        style={{ background: 'rgba(0,0,0,0.5)' }}
        onClick={e => e.stopPropagation()}>
        <span className="text-white/60 text-sm">
          {current + 1} / {images.length}
        </span>
        <span className="text-white text-sm truncate max-w-xs">{img.desc || ''}</span>
        <button onClick={onClose} className="text-white/70 hover:text-white">
          <X size={20} />
        </button>
      </div>

      {/* Zone image */}
      <div
        className="flex items-center justify-center w-full h-full overflow-hidden"
        onClick={onClose}
        onWheel={handleWheel}
      >
        <img
          src={img.url} alt={img.desc || ''}
          onClick={e => e.stopPropagation()}
          style={{
            transform: `scale(${scale})`,
            transition: 'transform 0.15s ease',
            maxWidth: '90vw',
            maxHeight: '80vh',
            objectFit: 'contain',
            cursor: scale > 1 ? 'grab' : 'default',
          }}
        />
      </div>

      {/* Flèche précédente */}
      {images.length > 1 && (
        <button
          onClick={e => { e.stopPropagation(); prev(); }}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/70 rounded-full p-2"
        >
          <ChevronLeft size={28} />
        </button>
      )}

      {/* Flèche suivante */}
      {images.length > 1 && (
        <button
          onClick={e => { e.stopPropagation(); next(); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/70 rounded-full p-2"
        >
          <ChevronRight size={28} />
        </button>
      )}

      {/* Barre basse : zoom */}
      <div className="absolute bottom-4 flex items-center gap-3 px-4 py-2 rounded-full"
        style={{ background: 'rgba(0,0,0,0.5)' }}
        onClick={e => e.stopPropagation()}>
        <button onClick={zoomOut} className="text-white/70 hover:text-white"><ZoomOut size={18} /></button>
        <span className="text-white/60 text-xs w-10 text-center">{Math.round(scale * 100)}%</span>
        <button onClick={zoomIn}  className="text-white/70 hover:text-white"><ZoomIn  size={18} /></button>
      </div>
    </div>
  );
};

export const FamilyDetailPage = () => {
  const { handle } = useParams();
  const navigate   = useNavigate();
  const [family,  setFamily]  = useState(null);
  const [events,  setEvents]  = useState([]);
  const [children,setChildren]= useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);
  const [medias,   setMedias]   = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        // 1. Famille
        const { data: fam } = await axios.get(`${API_URL}/api/families/${handle}`, { withCredentials: true });
        setFamily(fam);

        const jd = typeof fam.json_data === 'string' ? JSON.parse(fam.json_data) : (fam.json_data || {});

        // 2. Événements de la famille
        const eventRefs = (jd.event_ref_list || []).map(r => r?.ref || r).filter(Boolean);
        if (eventRefs.length) {
          const evRes = await Promise.all(
            eventRefs.map(ref => axios.get(`${API_URL}/api/events/${ref}`, { withCredentials: true }).catch(() => null))
          );
          const evList = await Promise.all(
            evRes.filter(Boolean).map(async r => {
              const ev = r.data;
              const evJd = typeof ev.json_data === 'string' ? JSON.parse(ev.json_data) : (ev.json_data || {});

              // Résolution du lieu via son handle
              let place_name = '';
              if (ev.place) {
                try {
                  const { data: pl } = await axios.get(`${API_URL}/api/places/${ev.place}`, { withCredentials: true });
                  const plJd = typeof pl.json_data === 'string' ? JSON.parse(pl.json_data) : (pl.json_data || {});
                  place_name = plJd?.name?.value || pl.title || '';
                } catch {
                  place_name = '';
                }
              }

              return {
                ...ev,
                event_type: EVENT_TYPES[evJd?.type?.value] || evJd?.type?.string || 'Événement',
                date_str:   parseDate(evJd?.date),
                place_name,
              };
            })
          );
          setEvents(evList);
        }

        // 3. Enfants
        const childRefs = (jd.child_ref_list || []).map(r => r?.ref || r).filter(Boolean);
        if (childRefs.length) {
          const chRes = await Promise.all(
            childRefs.map(ref => axios.get(`${API_URL}/api/persons/${ref}`, { withCredentials: true }).catch(() => null))
          );
          setChildren(chRes.filter(Boolean).map(r => r.data));
        }
        // 4. Médias de la famille
        try {
          const { data: meds } = await axios.get(
            `${API_URL}/api/media/family/${handle}`,
            { withCredentials: true }
          );
          setMedias(meds.filter(m => m.url));
        } catch {
          setMedias([]);
        }
      } catch (e) {
        console.error(e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [handle]);

  if (loading) return <div className="text-center py-12 text-stone-400">Chargement…</div>;
  if (error || !family) return <div className="text-center py-12 text-stone-400">Famille introuvable</div>;

  const hasFather = family.father_handle && family.father_given_name;
  const hasMother = family.mother_handle && family.mother_given_name;

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate('/families')} className="text-terracotta hover:text-terracotta-light -ml-2">
        <ArrowLeft size={18} className="mr-2" /> Retour aux familles
      </Button>

      <div className="bg-white border border-parchment-dark p-8">
        {/* En-tête */}
        <div className="flex items-center gap-4 mb-8">
          <Heart className="text-terracotta shrink-0" size={32} />
          <div>
            <h1 className="text-3xl font-bold text-forest" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {hasFather ? `${family.father_given_name} ${family.father_surname}` : '?'}
              {' & '}
              {hasMother ? `${family.mother_given_name} ${family.mother_surname}` : '?'}
            </h1>
            {family.gramps_id && <p className="text-sm text-stone-400 mt-1">ID : {family.gramps_id}</p>}
          </div>
        </div>

        <Accordion type="multiple" defaultValue={['parents', 'events', 'children', 'medias']} className="space-y-2">

          {/* Parents */}
          <AccordionItem value="parents" className="border border-parchment-dark">
            <AccordionTrigger className="px-6 hover:no-underline hover:bg-stone-50">
              <div className="flex items-center gap-3">
                <Users size={18} className="text-forest" />
                <span className="font-semibold text-forest">Les époux</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 py-4 bg-stone-50">
              <div className="space-y-3">
                {hasFather && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-stone-400 w-12 shrink-0">Père</span>
                    <PersonLink handle={family.father_handle} given_name={family.father_given_name} surname={family.father_surname} />
                  </div>
                )}
                {hasMother && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-stone-400 w-12 shrink-0">Mère</span>
                    <PersonLink handle={family.mother_handle} given_name={family.mother_given_name} surname={family.mother_surname} />
                  </div>
                )}
                {!hasFather && !hasMother && <p className="text-stone-400 text-sm">Aucun parent enregistré</p>}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Événements */}
          {events.length > 0 && (
            <AccordionItem value="events" className="border border-parchment-dark">
              <AccordionTrigger className="px-6 hover:no-underline hover:bg-stone-50">
                <div className="flex items-center gap-3">
                  <Calendar size={18} className="text-terracotta" />
                  <span className="font-semibold text-forest">Événements ({events.length})</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 py-4 bg-stone-50">
                <div className="space-y-3">
                  {events.map((ev, i) => (
                    <div key={i} className="border-l-2 border-terracotta pl-4">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-stone-900 text-sm">{ev.event_type}</span>
                        {ev.date_str && <span className="text-xs text-stone-400">{ev.date_str}</span>}
                      </div>
                      {ev.place_name && (
                        <div className="flex items-center gap-1 text-xs text-stone-500 mt-1">
                          <MapPin size={11} className="text-terracotta" /> {ev.place_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Enfants */}
          <AccordionItem value="children" className="border border-parchment-dark">
            <AccordionTrigger className="px-6 hover:no-underline hover:bg-stone-50">
              <div className="flex items-center gap-3">
                <Users size={18} className="text-forest" />
                <span className="font-semibold text-forest">
                  Enfants {children.length > 0 ? `(${children.length})` : ''}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 py-4 bg-stone-50">
              {children.length === 0 ? (
                <p className="text-stone-400 text-sm">Aucun enfant enregistré</p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {children.map((child, i) => (
                    <PersonLink key={i} handle={child.handle} given_name={child.given_name} surname={child.surname} />
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
          {/* Médias */}
          {medias.length > 0 && (
            <AccordionItem value="medias" className="border border-parchment-dark">
              <AccordionTrigger className="px-6 hover:no-underline hover:bg-stone-50">
                <div className="flex items-center gap-3">
                  <Image size={18} className="text-forest" />
                  <span className="font-semibold text-forest">Médias ({medias.length})</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 py-4 bg-stone-50">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {medias.map((img, i) => (
                    <button key={img.handle} onClick={() => setLightbox(i)}
                      className="group relative aspect-square overflow-hidden rounded border border-parchment-dark bg-stone-100 hover:border-terracotta transition-colors">
                      <img src={img.url} alt={img.desc || ''}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                    </button>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}


        </Accordion>
        {lightbox !== null && (
          <LightBox images={medias} index={lightbox} onClose={() => setLightbox(null)} />
        )}
      </div>
    </div>
  );
};